import { useEffect, useState } from 'react';
import styled from 'styled-components';

const NotificationOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const NotificationCard = styled.div`
  background: linear-gradient(135deg, #1a1a2e, #0f0f23);
  border: 2px solid #10b981;
  border-radius: 1.5rem;
  padding: 3rem;
  max-width: 500px;
  width: 90%;
  text-align: center;
  position: relative;
  animation: slideUp 0.5s ease;
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(50px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
  font-size: 2.5rem;
  animation: scaleIn 0.5s ease 0.2s both;
  
  @keyframes scaleIn {
    from { 
      opacity: 0;
      transform: scale(0);
    }
    to { 
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #10b981, #059669);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: slideUp 0.5s ease 0.3s both;
`;

const Message = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin-bottom: 2rem;
  line-height: 1.6;
  animation: slideUp 0.5s ease 0.4s both;
`;

const OrderDetails = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 2rem;
  text-align: left;
  animation: slideUp 0.5s ease 0.5s both;
  
  .detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
    
    &:last-child {
      margin-bottom: 0;
      padding-top: 0.75rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      font-weight: 700;
      font-size: 1.1rem;
      color: #10b981;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  animation: slideUp 0.5s ease 0.6s both;
  
  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 0.95rem;
  
  &.primary {
    background: linear-gradient(135deg, #10b981, #059669);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px rgba(16, 185, 129, 0.3);
    }
  }
  
  &.secondary {
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    color: white;
    transform: rotate(90deg);
  }
`;

function Notification({ orderData, onClose, isVisible }) {
  const [autoClose, setAutoClose] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Auto-fermeture après 10 secondes
      const timer = setTimeout(() => {
        setAutoClose(true);
        onClose();
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <NotificationOverlay>
      <NotificationCard>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <SuccessIcon>✓</SuccessIcon>
        
        <Title>Commande confirmée !</Title>
        
        <Message>
          Merci pour votre confiance, {orderData.customer.firstName} ! 
          Votre commande a été validée et vous recevrez une confirmation email dans quelques instants.
        </Message>
        
        <OrderDetails>
          <div className="detail-row">
            <span>Numéro de commande:</span>
            <span>#{orderData.id}</span>
          </div>
          <div className="detail-row">
            <span>Produit:</span>
            <span>{orderData.productName}</span>
          </div>
          <div className="detail-row">
            <span>Configuration:</span>
            <span>{orderData.storage} • {orderData.color}</span>
          </div>
          <div className="detail-row">
            <span>Livraison:</span>
            <span>{orderData.deliveryOption}</span>
          </div>
          <div className="detail-row">
            <span>Date:</span>
            <span>{formatDate(orderData.timestamp)}</span>
          </div>
          <div className="detail-row">
            <span>Total payé:</span>
            <span>{orderData.totalPrice.toLocaleString()} FCFA</span>
          </div>
        </OrderDetails>
        
        <ButtonGroup>
          <Button className="primary" onClick={() => window.location.href = '/'}>
            Continuer vos achats
          </Button>
          <Button className="secondary" onClick={onClose}>
            Voir les détails
          </Button>
        </ButtonGroup>
      </NotificationCard>
    </NotificationOverlay>
  );
}

export default Notification;
